import { useCallback, useContext, useMemo, useState } from 'react';
import { useHttpClient } from '../client/HttpClientContext';
import { fetchDecryptedHighlySensitivePropertyValue } from '../values/api';
import { HighlySensitivePropertyContext } from './HighlySensitivePropertyContext';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const toCacheKey = ({
  objectTypeId,
  objectId,
  propertyName,
  timestamp
}) => {
  return `${objectTypeId}/${objectId}/${propertyName}/${timestamp}`;
};
export const HighlySensitivePropertyContextProvider = props => {
  const parentContext = useContext(HighlySensitivePropertyContext);
  const httpClient = useHttpClient();
  const [propertyValuesMap, setPropertyValuesMap] = useState(new Map());
  const getHighlySensitivePropertyValue = useCallback(propertyValueIdentifier => {
    const key = toCacheKey(propertyValueIdentifier);
    return propertyValuesMap.get(key);
  }, [propertyValuesMap]);
  const fetchHighlySensitivePropertyValue = useCallback(propertyValueIdentifier => {
    const key = toCacheKey(propertyValueIdentifier);
    const propertyValue = propertyValuesMap.get(key);
    if ((propertyValue === null || propertyValue === void 0 ? void 0 : propertyValue.status) === 'LOADING' || (propertyValue === null || propertyValue === void 0 ? void 0 : propertyValue.status) === 'SUCCEEDED') {
      return propertyValue.promise;
    }
    const {
      objectTypeId,
      objectId,
      propertyName,
      timestamp
    } = propertyValueIdentifier;
    const fetchPromise = fetchDecryptedHighlySensitivePropertyValue({
      objectTypeId,
      objectId,
      propertyName,
      timestamp,
      httpClient
    });
    setPropertyValuesMap(prev => {
      return new Map(prev).set(key, Object.assign({}, propertyValueIdentifier, {
        promise: fetchPromise,
        status: 'LOADING'
      }));
    });
    return fetchPromise.then(value => {
      setPropertyValuesMap(prev => {
        return new Map(prev).set(key, Object.assign({}, propertyValueIdentifier, {
          promise: fetchPromise,
          status: 'SUCCEEDED',
          value
        }));
      });
      return value;
    }, error => {
      setPropertyValuesMap(prev => {
        return new Map(prev).set(key, Object.assign({}, propertyValueIdentifier, {
          status: 'FAILED',
          error
        }));
      });
      throw error;
    });
  }, [httpClient, propertyValuesMap]);
  const clearHighlySensitivePropertyValues = useCallback(({
    objectTypeId,
    objectId
  }) => {
    setPropertyValuesMap(prev => {
      const filteredMap = new Map(prev);
      for (const [key, highlySensitivePropertyValue] of filteredMap.entries()) {
        if (highlySensitivePropertyValue.objectTypeId === objectTypeId && highlySensitivePropertyValue.objectId === objectId) {
          filteredMap.delete(key);
        }
      }
      return filteredMap;
    });
  }, []);
  const contextValue = useMemo(() => ({
    propertyValuesMap,
    fetchHighlySensitivePropertyValue,
    getHighlySensitivePropertyValue,
    clearHighlySensitivePropertyValues
  }), [propertyValuesMap, fetchHighlySensitivePropertyValue, getHighlySensitivePropertyValue, clearHighlySensitivePropertyValues]);

  // Prefer the highest level context in the tree.
  if (parentContext !== undefined) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: props.children
    });
  }
  return /*#__PURE__*/_jsx(HighlySensitivePropertyContext.Provider, {
    value: contextValue,
    children: props.children
  });
};